import { queryOptions, useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { privateV2Api } from '@/lib/api-clients/rest-client';
import { useAccountStore } from '@/store/use-account-store';
import { AccountResponseV2 } from '@/types/api';

export const getAccountV2 = async (
  apiKey?: string,
): Promise<AccountResponseV2> => {
  return privateV2Api.get<AccountResponseV2>('/v2/account', {
    params: {},
    ...(apiKey
      ? {
          headers: {
            'X-API-KEY': apiKey,
          },
        }
      : {}),
  });
};

export const getAccountV2QueryOptions = (
  address?: string,
  apiKeyOverride?: string,
) => {
  const apiKey =
    apiKeyOverride ?? useAccountStore.getState().getAuthParams(address)?.apiKey;
  return queryOptions({
    queryKey: ['/account', address, apiKey], // Use apiKey instead of signingKey
    queryFn: () => getAccountV2(apiKey),
  });
};

type UseFetchAccountOptions = {
  address?: string | null;
  disabled?: boolean;
};

export default function useFetchAccountV2({
  address: forcedAddress,
  disabled,
}: UseFetchAccountOptions = {}) {
  const {
    address: storeAddress,
    hasAuth,
    apiKey,
    accGroup,
  } = useAccountStore(
    useShallow((state) => {
      const authParams = state.getAuthParams(forcedAddress ?? state.address);
      return {
        address: state.address,
        hasAuth: state.hasAuth,
        apiKey: authParams?.apiKey,
        accGroup: authParams?.accountGroup,
      };
    }),
  );

  const address = forcedAddress ?? storeAddress;

  const result = useQuery({
    enabled: !disabled && hasAuth && !!address && !!apiKey && accGroup === 2,
    staleTime: 0,
    refetchOnWindowFocus: false,
    ...getAccountV2QueryOptions(address, apiKey),
  });

  return result;
}
